/**
 * Apollo Provider for AI Services with offline-first caching and error handling
 *
 * Platform-specific behavior:
 * - Web: Uses browser's native online/offline detection (no additional checks)
 * - Native (iOS/Android): Performs actual connectivity tests to external endpoints
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { ApolloProvider, ApolloClient } from '@apollo/client';
import {
  createAIApolloClient,
  defaultAIClientConfig,
  AIClientConfig,
} from '../client/apollo-client';
import { testInternetConnectivity } from '../utils/connectivity';

// Context for AI Apollo Client configuration
interface AIApolloContextValue {
  client: ApolloClient<any>;
  isOnline: boolean;
  reconnect: () => Promise<void>;
  updateConfig: (config: Partial<AIClientConfig>) => void;
}

const AIApolloContext = createContext<AIApolloContextValue | null>(null);

// Hook to use AI Apollo context
export function useAIApolloContext() {
  const context = useContext(AIApolloContext);
  if (!context) {
    throw new Error('useAIApolloContext must be used within AIApolloProvider');
  }
  return context;
}

// Props for AI Apollo Provider
interface AIApolloProviderProps {
  children: React.ReactNode;
  config?: Partial<AIClientConfig>;
  onError?: (error: any) => void;
  onNetworkStatusChange?: (isOnline: boolean) => void;
}

// AI Apollo Provider component
export function AIApolloProvider({
  children,
  config = {},
  onError,
  onNetworkStatusChange,
}: AIApolloProviderProps) {
  const [client, setClient] = useState<ApolloClient<any>>();
  const [isOnline, setIsOnline] = useState(true);
  const [clientConfig, setClientConfig] = useState<AIClientConfig>({
    ...defaultAIClientConfig,
    ...config,
    onError,
  });

  // Initialize Apollo client
  useEffect(() => {
    const apolloClient = createAIApolloClient(clientConfig);
    setClient(apolloClient);
  }, [clientConfig]);

  // Function to test actual internet connectivity (native only)
  const testConnectivity = async (): Promise<boolean> => {
    return await testInternetConnectivity({
      timeout: 5000,
      retries: 1,
    });
  };

  // Handle network status changes with actual connectivity testing
  // Note: On web, this relies on browser's native online/offline events only
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let connectivityCheckInterval: ReturnType<typeof setInterval>;

    const updateConnectionStatus = async () => {
      // On web, just use navigator.onLine; on native, do actual connectivity test
      const actuallyOnline = await testConnectivity();

      if (actuallyOnline !== isOnline) {
        setIsOnline(actuallyOnline);
        onNetworkStatusChange?.(actuallyOnline);

        // Refetch important queries when coming back online
        if (actuallyOnline && client) {
          client.refetchQueries({
            include: ['contentRecommendations', 'journeyRecommendations'],
          });
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateConnectionStatus();
      }
    };

    const handleFocus = () => {
      updateConnectionStatus();
    };

    // Initial connectivity check
    updateConnectionStatus();

    // Periodic connectivity checks (every 30 seconds)
    connectivityCheckInterval = setInterval(updateConnectionStatus, 30000);

    // Check connectivity when page becomes visible or focused
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Still listen to browser events as a fallback, but verify with actual test
    const handleOnline = () => updateConnectionStatus();
    const handleOffline = () => updateConnectionStatus();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearInterval(connectivityCheckInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [client, onNetworkStatusChange, isOnline]);

  // Reconnect function with connectivity test
  const reconnect = async () => {
    if (client) {
      const actuallyOnline = await testConnectivity();
      setIsOnline(actuallyOnline);
      onNetworkStatusChange?.(actuallyOnline);

      if (actuallyOnline) {
        client.resetStore();
      }
    }
  };

  // Update configuration function
  const updateConfig = (newConfig: Partial<AIClientConfig>) => {
    setClientConfig(prev => ({ ...prev, ...newConfig }));
  };

  // Context value
  const contextValue: AIApolloContextValue = {
    client: client!,
    isOnline,
    reconnect,
    updateConfig,
  };

  if (!client) {
    return null;
  }

  return (
    <AIApolloContext.Provider value={contextValue}>
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </AIApolloContext.Provider>
  );
}

// Higher-order component for AI Apollo Provider
export function withAIApollo<P extends object>(
  Component: React.ComponentType<P>,
  config?: Partial<AIClientConfig>
) {
  return function WithAIApolloComponent(props: P) {
    return (
      <AIApolloProvider config={config}>
        <Component {...props} />
      </AIApolloProvider>
    );
  };
}

// Hook for network status
export function useNetworkStatus() {
  const { isOnline } = useAIApolloContext();
  return { isOnline, isOffline: !isOnline };
}

// Hook for Apollo client reconnection
export function useApolloReconnect() {
  const { reconnect } = useAIApolloContext();
  return reconnect;
}

// Hook for dynamic configuration updates
export function useApolloConfigUpdate() {
  const { updateConfig } = useAIApolloContext();
  return updateConfig;
}

// Component for displaying network status
export function NetworkStatusIndicator() {
  const { isOnline } = useNetworkStatus();

  if (isOnline) {
    return null; // Don't show anything when online
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: '#f59e0b',
        color: 'white',
        padding: '8px',
        textAlign: 'center',
        fontSize: '14px',
        zIndex: 9999,
      }}
    >
      You're offline. Some features may be limited.
    </div>
  );
}

// Component for handling Apollo errors
interface ApolloErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; retry: () => void }>;
}

export function ApolloErrorBoundary({
  children,
  fallback: Fallback,
}: ApolloErrorBoundaryProps) {
  const [error, setError] = useState<Error | null>(null);
  const { reconnect } = useAIApolloContext();

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (event.error?.networkError || event.error?.graphQLErrors) {
        setError(event.error);
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  const retry = () => {
    setError(null);
    reconnect();
  };

  if (error) {
    if (Fallback) {
      return <Fallback error={error} retry={retry} />;
    }

    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Something went wrong with AI services</h3>
        <p>{error.message}</p>
        <button
          onClick={retry}
          style={{ padding: '8px 16px', marginTop: '10px' }}
        >
          Retry
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

// Default error fallback component
export function DefaultApolloErrorFallback({
  error,
  retry,
}: {
  error: Error;
  retry: () => void;
}) {
  return (
    <div
      style={{
        padding: '20px',
        margin: '20px',
        border: '1px solid #ef4444',
        borderRadius: '8px',
        backgroundColor: '#fef2f2',
        color: '#dc2626',
      }}
    >
      <h4 style={{ margin: '0 0 10px 0' }}>AI Service Error</h4>
      <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>{error.message}</p>
      <button
        onClick={retry}
        style={{
          padding: '8px 16px',
          backgroundColor: '#dc2626',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        Retry Connection
      </button>
    </div>
  );
}
